import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const ResetPassword = () => {
  const { user, loading, configured, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Set new password · Olivia Flemming";
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const { error: err } = await updatePassword(password);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    setDone(true);
    setTimeout(() => navigate("/admin/dashboard", { replace: true }), 1200);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <h1 className="font-playfair text-3xl text-white text-center mb-2">Set new password</h1>
        <p className="text-white/50 text-sm text-center mb-10 font-inter">
          Choose a strong password for the admin portal.
        </p>

        {!configured && (
          <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
            Supabase env vars are missing.
          </div>
        )}

        {!loading && !user && (
          <div className="mb-6 rounded-xl border border-white/15 bg-secondary px-4 py-3 text-sm text-white/70 font-inter">
            Open this page from the password-reset email link so Supabase can establish a recovery session.
            Then set your new password here.
          </div>
        )}

        {done ? (
          <p className="text-center text-primary font-inter text-sm">Password updated. Redirecting…</p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                New password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl bg-secondary border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="confirm" className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl bg-secondary border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
              />
            </div>
            {error && (
              <p className="text-sm text-red-300" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting || !configured || !user}
              className="w-full rounded-full bg-primary text-black font-medium py-3.5 text-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Update password"}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/admin" className="text-sm text-white/50 hover:text-primary font-inter">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
